/**
 * BatchSummaryBar
 *
 * Pinned stats bar above the batch results table.
 * Shows verdict counts (GO / NO-GO / CONDITIONAL / PENDING),
 * average composite score, and best deal highlight.
 */

import { Trophy } from 'lucide-react';
import type { BatchPropertyResult } from '../../types/batch';

export interface BatchSummaryBarProps {
  results: BatchPropertyResult[];
}

export function BatchSummaryBar({ results }: BatchSummaryBarProps) {
  // Verdict counts
  let goCount = 0;
  let nogoCount = 0;
  let conditionalCount = 0;
  let pendingCount = 0;

  // Composite score stats
  let scoreSum = 0;
  let scoreCount = 0;
  let bestResult: BatchPropertyResult | null = null;
  let bestScore = -Infinity;

  for (const r of results) {
    // Count verdicts
    if (r.deal_verdict === 'GO') goCount++;
    else if (r.deal_verdict === 'NO-GO') nogoCount++;
    else if (r.deal_verdict === 'CONDITIONAL') conditionalCount++;
    else pendingCount++;

    // Composite score stats
    if (r.composite_score != null) {
      scoreSum += r.composite_score;
      scoreCount++;
      if (r.composite_score > bestScore) {
        bestScore = r.composite_score;
        bestResult = r;
      }
    }
  }

  const avgScore = scoreCount > 0 ? scoreSum / scoreCount : null;

  // Truncate long address for the "best deal" pill
  const bestAddress = bestResult?.address
    ? bestResult.address.length > 28
      ? bestResult.address.slice(0, 26) + '…'
      : bestResult.address
    : null;

  return (
    <div className="bg-bg-card border border-border rounded-lg px-4 py-3 mb-4">
      <div className="flex flex-wrap items-center gap-2 text-xs font-medium">

        {/* GO count */}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          GO: {goCount}
        </span>

        {/* NO-GO count */}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/25">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
          NO-GO: {nogoCount}
        </span>

        {/* CONDITIONAL count */}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          CONDITIONAL: {conditionalCount}
        </span>

        {/* PENDING count (Stage 1 only) */}
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-500/15 text-text-secondary border border-border">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
            PENDING: {pendingCount}
          </span>
        )}

        {/* Divider */}
        <span className="w-px h-4 bg-border mx-1" />

        {/* Average composite score */}
        {avgScore !== null && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent/10 text-accent border border-accent/20">
            Avg Score: {avgScore.toFixed(1)}
          </span>
        )}

        {/* Best deal */}
        {bestAddress && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent/10 text-accent border border-accent/20">
            <Trophy className="w-3 h-3" />
            Best: {bestAddress} ({bestScore.toFixed(1)})
          </span>
        )}

        {/* Total count */}
        <span className="ml-auto text-text-secondary">
          {results.length} propert{results.length === 1 ? 'y' : 'ies'}
        </span>
      </div>
    </div>
  );
}

export default BatchSummaryBar;
